import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, X, Loader2, Paperclip, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024; // 2 MB real

export default function FeedbackButton() {
  const { api, user } = useAuth();
  const location = useLocation();
  const [isBeta, setIsBeta] = useState(false);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null); // data URL
  const [videoUrl, setVideoUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const detectVideoProvider = (url) => {
    const u = (url || '').toLowerCase();
    if (u.includes('loom.com')) return { name: 'Loom', color: 'from-violet-500 to-purple-500' };
    if (u.includes('wistia.com') || u.includes('wistia.net')) return { name: 'Wistia', color: 'from-sky-500 to-blue-600' };
    if (u.includes('youtube.com') || u.includes('youtu.be')) return { name: 'YouTube', color: 'from-red-500 to-rose-600' };
    if (u.includes('vimeo.com')) return { name: 'Vimeo', color: 'from-cyan-500 to-teal-500' };
    if (u.startsWith('http')) return { name: 'Video', color: 'from-slate-500 to-slate-600' };
    return null;
  };
  const provider = detectVideoProvider(videoUrl);

  const skipOnPath = /^\/(login|signup|set-password|beta-onboarding|billing-setup)/i.test(location.pathname || '');

  useEffect(() => {
    if (!user || skipOnPath) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/me/beta-status');
        if (!cancelled) setIsBeta(!!res.data?.is_beta);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, location.pathname]);

  const openModal = useCallback(() => setOpen(true), []);
  useEffect(() => {
    const handler = () => openModal();
    window.addEventListener('open-feedback-modal', handler);
    return () => window.removeEventListener('open-feedback-modal', handler);
  }, [openModal]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
      toast.error('Only PNG, JPG, WebP, or GIF images supported');
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      toast.error('Screenshot too large (max 2 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setScreenshot(String(e.target?.result || ''));
    reader.readAsDataURL(file);
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) { handleFile(file); e.preventDefault(); return; }
      }
    }
  };

  const submit = async () => {
    const msg = message.trim();
    if (msg.length < 3) return toast.error('Please enter a real message');
    const vu = videoUrl.trim();
    if (vu && !/^https?:\/\//i.test(vu)) return toast.error('Video link must start with https://');
    setSubmitting(true);
    try {
      await api.post('/api/feedback', {
        message: msg,
        category,
        screenshot: screenshot || undefined,
        video_url: vu || undefined,
      });
      toast.success('Thanks for the feedback — the team will see it right away.');
      setMessage('');
      setCategory('general');
      setScreenshot(null);
      setVideoUrl('');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isBeta || skipOnPath) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          data-testid="feedback-modal"
          onClick={() => !submitting && setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.94, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, opacity: 0 }}
            className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-gradient-to-b from-[#131519] to-[#0a0b0f] p-7 shadow-[0_30px_60px_-10px_rgba(0,0,0,0.85)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !submitting && setOpen(false)}
              className="absolute top-3 right-3 p-2 rounded-lg text-slate-500 hover:text-white hover:bg-white/5"
              aria-label="Close"
              data-testid="feedback-modal-close"
            >
              <X size={16} />
            </button>

            <div className="flex items-start gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-teal-400/10 border border-teal-400/20 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="text-teal-300" size={20} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Share feedback</h3>
                <p className="text-sm text-slate-400 mt-0.5">
                  Tell us what's working, what isn't, or what you wish existed. We read every message.
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Category</label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { k: 'general', label: 'General' },
                  { k: 'bug', label: 'Bug' },
                  { k: 'feature', label: 'Feature' },
                  { k: 'other', label: 'Other' },
                ].map((c) => (
                  <button
                    key={c.k}
                    onClick={() => setCategory(c.k)}
                    className={`py-2 rounded-lg text-[12px] font-medium transition-colors ${
                      category === c.k
                        ? 'bg-teal-400/20 text-teal-200 border border-teal-400/40'
                        : 'bg-white/[0.03] text-slate-300 border border-white/[0.06] hover:border-white/20'
                    }`}
                    data-testid={`feedback-cat-${c.k}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Video link <span className="text-slate-500 normal-case font-normal">(optional)</span></label>
                {provider && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold tracking-wider uppercase bg-gradient-to-r ${provider.color} text-white shadow-sm`}>
                    {provider.name}
                  </span>
                )}
              </div>
              <input
                type="url"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://loom.com/share/… or Wistia / YouTube link"
                className="w-full rounded-xl bg-[#0d0e12] border border-white/10 text-white placeholder-slate-500 px-3.5 py-2.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-teal-400/40"
                data-testid="feedback-video-input"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onPaste={onPaste}
                rows={6}
                maxLength={4000}
                placeholder="What's on your mind? (you can paste a screenshot directly here)"
                className="w-full rounded-xl bg-[#0d0e12] border border-white/10 text-white placeholder-slate-500 p-3.5 text-[14px] leading-relaxed focus:outline-none focus:ring-1 focus:ring-teal-400/40 resize-none"
                data-testid="feedback-textarea"
              />
              <div className="flex items-center justify-between mt-1">
                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
                    data-testid="feedback-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-teal-300 px-2 py-1 rounded border border-white/[0.06] hover:border-teal-400/30 transition-colors"
                    data-testid="feedback-attach-btn"
                  >
                    <Paperclip size={11} />
                    Attach screenshot
                  </button>
                </div>
                <div className="text-[11px] text-slate-500">{message.length}/4000</div>
              </div>

              {screenshot && (
                <div className="mt-3 relative rounded-xl border border-white/10 bg-[#0d0e12] overflow-hidden" data-testid="feedback-screenshot-preview">
                  <img src={screenshot} alt="Screenshot preview" className="w-full max-h-48 object-contain bg-black/40" />
                  <button
                    type="button"
                    onClick={() => setScreenshot(null)}
                    className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 text-white hover:bg-rose-500 transition-colors"
                    aria-label="Remove screenshot"
                    data-testid="feedback-screenshot-remove"
                  >
                    <X size={12} />
                  </button>
                  <div className="absolute bottom-2 left-2 flex items-center gap-1 text-[10px] text-white/80 bg-black/60 rounded px-1.5 py-0.5">
                    <ImageIcon size={10} /> {(screenshot.length * 0.75 / 1024).toFixed(0)} KB
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={() => !submitting && setOpen(false)}
                className="px-4 py-2.5 rounded-xl bg-white/[0.04] text-slate-300 hover:bg-white/[0.08] text-sm font-medium"
                data-testid="feedback-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting || message.trim().length < 3}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-teal-400 text-[#0a0b0f] font-semibold hover:bg-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="feedback-submit-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={14} />}
                Send feedback
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
